import { useState, useRef } from 'react'
import { useChatStore } from '../../store/chatStore'
import { useSocket } from '../../hooks/useSocket'
import { 
  PaperAirplaneIcon,
  PaperClipIcon,
  FaceSmileIcon,
  PhotoIcon
} from '@heroicons/react/24/outline'

const MessageInput = ({ chatId }) => {
  const [message, setMessage] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [replyTo, setReplyTo] = useState(null)
  
  const fileInputRef = useRef(null)
  const imageInputRef = useRef(null)
  const textareaRef = useRef(null)
  
  const { sendMessage } = useChatStore()
  const { sendMessage: socketSendMessage, startTyping, stopTyping } = useSocket()

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!message.trim() && !replyTo) return

    const messageData = {
      chatId,
      content: message.trim(),
      messageType: 'text',
      replyTo: replyTo?._id || null
    }

    // Send via socket for real-time (backend will handle persistence)
    socketSendMessage(messageData)
    
    // Reset form
    setMessage('')
    setReplyTo(null)
    setIsTyping(false)
    stopTyping(chatId)
    
    // Focus back to textarea
    textareaRef.current?.focus()
  }

  const handleInputChange = (e) => {
    setMessage(e.target.value)
    
    // Handle typing indicators
    if (e.target.value.trim() && !isTyping) {
      setIsTyping(true)
      startTyping(chatId)
    } else if (!e.target.value.trim() && isTyping) {
      setIsTyping(false)
      stopTyping(chatId)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  const handleFileUpload = async (files, type = 'file') => {
    if (!files || files.length === 0) return

    try {
      // TODO: Implement file upload
      console.log('Uploading files:', files, 'Type:', type)
      
      // For now, just show a placeholder
      const messageData = {
        chatId,
        content: `${files.length} ${type}(s) uploaded`,
        messageType: type,
        attachments: Array.from(files).map(file => ({
          filename: file.name,
          fileSize: file.size,
          fileType: file.type,
          url: URL.createObjectURL(file) // Temporary URL for preview
        }))
      }

      socketSendMessage(messageData)
      
    } catch (error) {
      console.error('File upload error:', error)
    }
  }

  const insertEmoji = (emoji) => {
    setMessage(prev => prev + emoji)
    setShowEmojiPicker(false)
    textareaRef.current?.focus()
  }

  const commonEmojis = ['😀', '😂', '❤️', '👍', '👎', '😍', '🤔', '😢', '😮', '🎉', '🔥', '💯']

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
      {/* Reply Preview */}
      {replyTo && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Replying to {replyTo.sender.username}
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-300 truncate">
                {replyTo.content}
              </p>
            </div>
            <button
              onClick={() => setReplyTo(null)}
              className="ml-2 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
            >
              <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="p-4">
        <form onSubmit={handleSubmit} className="flex items-end space-x-3">
          {/* File Upload Buttons */}
          <div className="flex items-center space-x-1">
            {/* Image Upload */}
            <button
              type="button"
              onClick={() => imageInputRef.current?.click()}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title="Upload image"
            >
              <PhotoIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </button>
            
            {/* File Upload */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title="Upload file"
            >
              <PaperClipIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          {/* Message Input */}
          <div className="flex-1 relative">
            <textarea
              ref={textareaRef}
              value={message}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              rows={1}
              className="w-full px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg resize-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              style={{ minHeight: '48px', maxHeight: '120px' }}
            />
            
            {/* Emoji Button */}
            <div className="absolute right-3 bottom-3">
              <button
                type="button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                <FaceSmileIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>

              {/* Simple Emoji Picker */}
              {showEmojiPicker && (
                <>
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setShowEmojiPicker(false)}
                  />
                  <div className="absolute bottom-full right-0 mb-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-3 z-20">
                    <div className="grid grid-cols-6 gap-2">
                      {commonEmojis.map(emoji => (
                        <button
                          key={emoji}
                          type="button"
                          onClick={() => insertEmoji(emoji)}
                          className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-lg"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Send Button */}
          <button
            type="submit"
            disabled={!message.trim() && !replyTo}
            className={`
              p-3 rounded-lg transition-colors
              ${message.trim() || replyTo
                ? 'bg-primary-600 hover:bg-primary-700 text-white' 
                : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
              }
            `}
          >
            <PaperAirplaneIcon className="h-5 w-5" />
          </button>

          {/* Hidden File Inputs */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => handleFileUpload(e.target.files, 'file')}
            accept=".pdf,.doc,.docx,.txt,.zip,.rar"
          />
          
          <input
            ref={imageInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => handleFileUpload(e.target.files, 'image')}
            accept="image/*"
          />
        </form>
      </div>
    </div>
  )
}

export default MessageInput